import type {
  DynamicFormFieldConfig,
  QuestionnaireFormComponentRegistry,
} from '@main/dynamic-form';

import {
  ControlsTextSearchDetailsFragment,
  EvidenceTextSearchDetailsFragment,
  PoliciesTextSearchDetailsFragment,
  RisksTextSearchDetailsFragment,
  TasksTextSearchDetailsFragment,
  VendorsTextSearchDetailsFragment,
} from './fragments/TextSearchFragment.generated';
import type {
  Client_Questionnaire_Status_Enum,
  Comments,
  Control_Categories,
  Control_Criteria,
  Control_Evidences,
  Control_Groups,
  Control_Owners,
  Control_Tags,
  Control_Tasks,
  Controls,
  Date_Field_Values,
  Email_Field_Values,
  Entity_Relations,
  Evidence_Acl,
  Evidence_Owners,
  Evidence_Policies,
  Evidence_Tags,
  Evidence_Tasks,
  Evidence_Version_File,
  Evidence_Versions,
  Evidences,
  Field_Configs,
  Finding_Types_Enum,
  Integration_Names_Enum,
  Number_Field_Values,
  Policies,
  Policy_Acknowledgement_Statuses_Enum,
  Policy_Acknowledgement_Users,
  Policy_Acknowledgements,
  Policy_Approval_Statuses_Enum,
  Policy_Approval_Users,
  Policy_Approvals,
  Policy_Exception_Approvers,
  Policy_Exceptions,
  Policy_Integrations,
  Policy_Owners,
  Policy_Tasks,
  Policy_Version_Statuses_Enum,
  Policy_Versions,
  Reviews,
  Risk_Approval_Statuses_Enum,
  Risk_Approvals,
  Risk_Approvers,
  Risk_Controls,
  Risk_Owners,
  Risk_Tasks,
  Risks,
  SearchEntitiesEnum,
  Select_Field_Values,
  Task_Owners,
  Tasks,
  Text_Field_Values,
  Url_Field_Values,
  Vendor_Approval_Statuses_Enum,
  Vendor_Approvals,
  Vendor_Approvers,
  Vendor_Owners,
  Vendor_Questionnaire_Statuses_Enum,
  Vendor_Questionnaires,
  Vendor_Risks,
  Vendor_Tasks,
  Vendors,
} from './types.generated';

export type { ColumnFiltersState, SortingState } from '@tanstack/react-table';

declare module 'graphql' {
  interface GraphQLErrorExtensions {
    code?: GraphqlExtensionsCode;
  }
}

export type FindingTypesEnum = Finding_Types_Enum;

export type UserMetaData = Partial<{
  firstName: string;
  lastName: string;
  isOnboardingFinished: boolean;
}>;

export type TrustPortalTheme = Partial<{
  colors: Partial<{
    background: string;
    primary: string;
    link: string;
  }>;
}>;

export enum UserRole {
  Admin = 'admin',
  User = 'user',
  Vendor = 'vendor',
  Public = 'public',
  Trust = 'trust',
}

type Properties = 'category' | 'group' | 'criteria';
export type ControlTags = Array<Record<Properties, string[]>>;

export enum ControlEvidenceStatus {
  CALCULATING = 'Calculating',
  VALID = 'Valid',
  INVALID = 'Invalid',
  AT_RISK = 'At risk',
}

export enum ControlStatus {
  PENDING = 'Pending',
  NOT_RELEVANT = 'Not relevant',
  VALID = 'Valid',
  AT_RISK = 'At risk',
  FAILING = 'Failing',
}

export enum TaskStatus {
  PENDING = 'Pending',
  IN_PROGRESS = 'In progress',
  COMPLETED = 'Completed',
  OVERDUE = 'Overdue',
}

export enum VendorAssessmentStatus {
  PENDING = 'Pending',
  REVIEWED = 'Reviewed',
  EXPIRING = 'Expiring',
  OVERDUE = 'Overdue',
  TERMINATED = 'Terminated',
}

export enum RiskAssessmentStatus {
  PENDING = 'Pending',
  REVIEWED = 'Reviewed',
  EXPIRING = 'Expiring',
  OVERDUE = 'Overdue',
  CLOSED = 'Closed',
}

export enum EvidenceStatus {
  PENDING = 'Pending',
  VALID = 'Valid',
  AT_RISK = 'At risk',
  INVALID = 'Invalid',
}

export enum EvidenceVersionType {
  File = 'file',
  Link = 'link',
  Policy = 'policy',
}

export type LinkedEntityAuditRowMetadata = {
  linked_entity_internal_id?: string;
  linked_entity_name?: string;
  linked_entity_was_deleted?: true;
};

export type EntityRelationAuditRow = {
  table_name: 'entity_relations';
  row_data: Entity_Relations;
  changed_fields: Entity_Relations;
  metadata?: LinkedEntityAuditRowMetadata;
};

type ApprovalRequestAuditRowMetadata = {
  approval_request_name?: string;
};

type CustomFieldAuditValue = string | number | string[] | null;

type CustomFieldAuditRowMetadata = {
  custom_field_deleted?: true;
  custom_field_id?: string;
  custom_field_name?: string;
  custom_field_type?: string;
  custom_field_old_value?: CustomFieldAuditValue;
  custom_field_new_value?: CustomFieldAuditValue;
};

type CustomFieldAuditRow =
  | {
      table_name: 'text_field_values';
      row_data: Text_Field_Values;
      changed_fields: Text_Field_Values;
      metadata?: CustomFieldAuditRowMetadata;
    }
  | {
      table_name: 'url_field_values';
      row_data: Url_Field_Values;
      changed_fields: Url_Field_Values;
      metadata?: CustomFieldAuditRowMetadata;
    }
  | {
      table_name: 'date_field_values';
      row_data: Date_Field_Values;
      changed_fields: Date_Field_Values;
      metadata?: CustomFieldAuditRowMetadata;
    }
  | {
      table_name: 'number_field_values';
      row_data: Number_Field_Values;
      changed_fields: Number_Field_Values;
      metadata?: CustomFieldAuditRowMetadata;
    }
  | {
      table_name: 'email_field_values';
      row_data: Email_Field_Values;
      changed_fields: Email_Field_Values;
      metadata?: CustomFieldAuditRowMetadata;
    }
  | {
      table_name: 'select_field_values';
      row_data: Select_Field_Values;
      changed_fields: Select_Field_Values;
      metadata?: CustomFieldAuditRowMetadata;
    }
  | {
      table_name: 'field_configs';
      row_data: Field_Configs;
      changed_fields: Field_Configs;
      metadata?: CustomFieldAuditRowMetadata;
    };

export type RiskAuditRow =
  | {
      table_name: 'risks';
      /* Extending the Type to preserve deprecated column for the Risks */
      row_data: Risks & {
        assignee_id: string | undefined;
      };
      /* Extending the Type to preserve deprecated column for the Risks */
      changed_fields: Risks & {
        last_reviewed_date: string | undefined;
        assignee_id: string | undefined;
      };
      metadata?: unknown;
    }
  | {
      table_name: 'risk_controls';
      row_data: Risk_Controls;
      changed_fields: Risk_Controls;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'risk_owners';
      row_data: Risk_Owners;
      changed_fields: Risk_Owners;
      metadata?: unknown;
    }
  | {
      table_name: 'vendor_risks';
      row_data: Vendor_Risks;
      changed_fields: Vendor_Risks;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'risk_tasks';
      row_data: Risk_Tasks;
      changed_fields: Risk_Tasks;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'comments';
      row_data: Comments;
      changed_fields: Comments;
      metadata?: unknown;
    }
  | {
      table_name: 'reviews';
      row_data: Reviews;
      changed_fields: Reviews;
      metadata?: unknown;
    }
  | {
      table_name: 'risk_approvals';
      row_data: Risk_Approvals;
      changed_fields: Risk_Approvals;
      metadata?: ApprovalRequestAuditRowMetadata;
    }
  | {
      table_name: 'risk_approvers';
      row_data: Risk_Approvers;
      changed_fields: Risk_Approvers;
      metadata?: ApprovalRequestAuditRowMetadata;
    }
  | EntityRelationAuditRow
  | CustomFieldAuditRow;
export type RiskRowData = RiskAuditRow['row_data'];
export type RiskHistoryTables = RiskAuditRow['table_name'];
export type RiskChangedFields = RiskAuditRow['changed_fields'];
export type RiskHistoryMetadata = RiskAuditRow['metadata'];

export type ControlAuditRow =
  | {
      table_name: 'controls';
      /* Extending the Type to preserve deprecated column for the Controls */
      row_data: Controls & {
        assignee_id: string | undefined;
      };
      changed_fields: Controls & {
        assignee_id: string | undefined;
      };
      metadata?: unknown;
    }
  | {
      table_name: 'risk_controls';
      row_data: Risk_Controls;
      changed_fields: Risk_Controls;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'control_evidences';
      row_data: Control_Evidences;
      changed_fields: Control_Evidences;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'control_tasks';
      row_data: Control_Tasks;
      changed_fields: Control_Tasks;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'control_tags';
      row_data: Control_Tags;
      changed_fields: Control_Tags;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'control_groups';
      row_data: Control_Groups;
      changed_fields: Control_Groups;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'control_criteria';
      row_data: Control_Criteria;
      changed_fields: Control_Criteria;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'control_categories';
      row_data: Control_Categories;
      changed_fields: Control_Categories;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'control_owners';
      row_data: Control_Owners;
      changed_fields: Control_Owners;
      metadata?: unknown;
    }
  | {
      table_name: 'comments';
      row_data: Comments;
      changed_fields: Comments;
      metadata?: unknown;
    }
  | EntityRelationAuditRow
  | CustomFieldAuditRow;
export type ControlRowData = ControlAuditRow['row_data'];
export type ControlHistoryTables = ControlAuditRow['table_name'];
export type ControlChangedFields = ControlAuditRow['changed_fields'];
export type ControlHistoryMetadata = ControlAuditRow['metadata'];

export type EvidenceVersionAuditRowMetadata = {
  evidence_version_name?: string;
};

export type EvidenceAuditRow =
  | {
      table_name: 'evidences';
      row_data: Evidences;
      changed_fields: Evidences;
      metadata?: unknown;
    }
  | {
      table_name: 'evidence_owners';
      row_data: Evidence_Owners;
      changed_fields: Evidence_Owners;
      metadata?: unknown;
    }
  | {
      table_name: 'evidence_versions';
      row_data: Evidence_Versions;
      changed_fields: Evidence_Versions;
      metadata?: EvidenceVersionAuditRowMetadata;
    }
  | {
      table_name: 'evidence_version_file';
      row_data: Evidence_Version_File;
      changed_fields: Evidence_Version_File;
      metadata?: EvidenceVersionAuditRowMetadata;
    }
  | {
      table_name: 'control_evidences';
      row_data: Control_Evidences;
      changed_fields: Control_Evidences;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'evidence_tags';
      row_data: Evidence_Tags;
      changed_fields: Evidence_Tags;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'evidence_tasks';
      row_data: Evidence_Tasks;
      changed_fields: Evidence_Tasks;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'evidence_policies';
      row_data: Evidence_Policies;
      changed_fields: Evidence_Policies;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'evidence_acl';
      row_data: Evidence_Acl;
      changed_fields: Evidence_Acl;
      metadata?: unknown;
    }
  | {
      table_name: 'comments';
      row_data: Comments;
      changed_fields: Comments;
      metadata?: unknown;
    }
  | EntityRelationAuditRow
  | CustomFieldAuditRow;
export type EvidenceRowData = EvidenceAuditRow['row_data'];
export type EvidenceHistoryTables = EvidenceAuditRow['table_name'];
export type EvidenceChangedFields = EvidenceAuditRow['changed_fields'];
export type EvidenceHistoryMetadata = EvidenceAuditRow['metadata'];

export type PolicyAuditRow =
  | {
      table_name: 'policies';
      /* Extending the Type to preserve deprecated column for historical policy rows */
      row_data: Policies & {
        assignee_id: string | undefined;
      };
      /* Extending the Type to preserve deprecated column for historical policy rows */
      changed_fields: Policies & {
        assignee_id: string | undefined;
      };
      metadata?: unknown;
    }
  | {
      table_name: 'policy_owners';
      row_data: Policy_Owners;
      changed_fields: Policy_Owners;
      metadata?: unknown;
    }
  | {
      table_name: 'policy_versions';
      row_data: Policy_Versions;
      changed_fields: Policy_Versions;
      metadata?: unknown;
    }
  | {
      table_name: 'policy_approvals';
      row_data: Policy_Approvals;
      changed_fields: Policy_Approvals;
      metadata?: {
        version_id?: string;
      };
    }
  | {
      table_name: 'policy_approval_users';
      row_data: Policy_Approval_Users;
      changed_fields: Policy_Approval_Users;
      metadata?: {
        version_id?: string;
      };
    }
  | {
      table_name: 'policy_exceptions';
      row_data: Policy_Exceptions;
      changed_fields: Policy_Exceptions;
      metadata?: {
        version_id?: string;
      };
    }
  | {
      table_name: 'policy_exception_approvers';
      row_data: Policy_Exception_Approvers;
      changed_fields: Policy_Exception_Approvers;
      metadata?: {
        exception_name?: string;
        version_id?: string;
      };
    }
  | {
      table_name: 'policy_tasks';
      row_data: Policy_Tasks;
      changed_fields: Policy_Tasks;
      metadata?: {
        task_internal_id?: string;
        task_name?: string;
        task_was_deleted?: true;
      };
    }
  | {
      table_name: 'evidence_policies';
      row_data: Evidence_Policies;
      changed_fields: Evidence_Policies;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'policy_acknowledgements';
      row_data: Policy_Acknowledgements;
      changed_fields: Policy_Acknowledgements;
      metadata?: {
        version_id?: string;
      };
    }
  | {
      table_name: 'policy_acknowledgement_users';
      row_data: Policy_Acknowledgement_Users;
      changed_fields: Policy_Acknowledgement_Users;
      metadata?: {
        version_id?: string;
      };
    }
  | {
      table_name: 'comments';
      row_data: Comments;
      changed_fields: Comments;
      metadata?: unknown;
    }
  | {
      table_name: 'policy_integrations';
      row_data: Pick<Policy_Integrations, 'config' | 'organization_integration_id'>;
      changed_fields: Partial<Pick<Policy_Integrations, 'config' | 'organization_integration_id'>>;
      metadata?: {
        provider_name?: Integration_Names_Enum | null;
        connector_name?: string | null;
      };
    }
  | EntityRelationAuditRow
  | CustomFieldAuditRow;
export type PolicyRowData = PolicyAuditRow['row_data'];
export type PolicyHistoryTables = PolicyAuditRow['table_name'];
export type PolicyChangedFields = PolicyAuditRow['changed_fields'];
export type PolicyHistoryMetadata = PolicyAuditRow['metadata'];

export type VendorQuestionnaireAuditRowData = Omit<Vendor_Questionnaires, 'send_source'> & {
  send_source?: 'manual' | 'scheduled';
};

export type VendorAuditRow =
  | {
      table_name: 'vendors';
      /* Extending the Type to preserve deprecated column for the Vendors */
      row_data: Vendors & {
        owner_id: string | undefined;
      };
      /* Extending the Type to preserve deprecated column for the Vendors */
      changed_fields: Vendors & {
        last_reviewed_date: string | undefined;
        owner_id: string | undefined;
      };
      metadata?: unknown;
    }
  | {
      table_name: 'vendor_questionnaires';
      row_data: VendorQuestionnaireAuditRowData;
      changed_fields: VendorQuestionnaireAuditRowData;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'vendor_risks';
      row_data: Vendor_Risks;
      changed_fields: Vendor_Risks;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'vendor_tasks';
      row_data: Vendor_Tasks;
      changed_fields: Vendor_Tasks;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'vendor_owners';
      row_data: Vendor_Owners;
      changed_fields: Vendor_Owners;
      metadata?: unknown;
    }
  | {
      table_name: 'comments';
      row_data: Comments;
      changed_fields: Comments;
      metadata?: unknown;
    }
  | {
      table_name: 'reviews';
      row_data: Reviews;
      changed_fields: Reviews;
      metadata?: unknown;
    }
  | {
      table_name: 'vendor_approvals';
      row_data: Vendor_Approvals;
      changed_fields: Vendor_Approvals;
      metadata?: ApprovalRequestAuditRowMetadata;
    }
  | {
      table_name: 'vendor_approvers';
      row_data: Vendor_Approvers;
      changed_fields: Vendor_Approvers;
      metadata?: ApprovalRequestAuditRowMetadata;
    }
  | EntityRelationAuditRow
  | CustomFieldAuditRow;
export type VendorRowData = VendorAuditRow['row_data'];
export type VendorHistoryTables = VendorAuditRow['table_name'];
export type VendorChangedFields = VendorAuditRow['changed_fields'];
export type VendorHistoryMetadata = VendorAuditRow['metadata'];

export type TaskAuditRow =
  | {
      table_name: 'tasks';
      row_data: Tasks;
      changed_fields: Tasks;
      metadata?: unknown;
    }
  | {
      table_name: 'task_owners';
      row_data: Task_Owners;
      changed_fields: Task_Owners;
      metadata?: unknown;
    }
  | {
      table_name: 'risk_tasks';
      row_data: Risk_Tasks;
      changed_fields: Risk_Tasks;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'vendor_tasks';
      row_data: Vendor_Tasks;
      changed_fields: Vendor_Tasks;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'policy_tasks';
      row_data: Policy_Tasks;
      changed_fields: Policy_Tasks;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'control_tasks';
      row_data: Control_Tasks;
      changed_fields: Control_Tasks;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'evidence_tasks';
      row_data: Evidence_Tasks;
      changed_fields: Evidence_Tasks;
      metadata?: LinkedEntityAuditRowMetadata;
    }
  | {
      table_name: 'comments';
      row_data: Comments;
      changed_fields: Comments;
      metadata?: unknown;
    }
  | EntityRelationAuditRow
  | CustomFieldAuditRow;

export type TaskRowData = TaskAuditRow['row_data'];
export type TaskHistoryTables = TaskAuditRow['table_name'];
export type TaskChangedFields = TaskAuditRow['changed_fields'];
export type TaskHistoryMetadata = TaskAuditRow['metadata'];

/**
 * Built-in Hasura session variables (always set by Hasura on user
 * sessions). Lifted to an enum so callers can reference them as a
 * single source of truth instead of restating the string literals.
 */
export enum DefaultHasuraSessionVariable {
  'x-hasura-user-id' = 'x-hasura-user-id',
  'x-hasura-role' = 'x-hasura-role',
}

/**
 * Custom hasura session variables we want to use in the audit table
 * needs to always have a prefix of `x-hasura-` to work
 */
export enum CustomSessionVariable {
  'x-hasura-impersonater-id' = 'x-hasura-impersonater-id',
  'x-hasura-replaced-role' = 'x-hasura-replaced-role',
  'x-hasura-agent-id' = 'x-hasura-agent-id',
  'x-hasura-mcp-client-id' = 'x-hasura-mcp-client-id',
}

export type AuditTableHasuraUser = {
  'x-hasura-user-id': string;
  data?: Pick<VendorQuestionnaireAuditRowData, 'send_source'>;
} & {
  [key in CustomSessionVariable]?: string;
} & (
    | {
        'x-hasura-role': 'user';
      }
    | { 'x-hasura-role': 'admin' }
  );

export type AuditTableActions = 'I' | 'U' | 'D' | 'T';

export type GraphqlExtensionsCode =
  | 'constraint-violation'
  | 'USER_ALREADY_EXISTS'
  | 'USER_ALREADY_INVITED'
  | 'USER_EMAIL_SSO_ENFORCEMENT'
  | 'INVALID_INPUT'
  | 'INTEGRATION_CONNECTOR_NAME_CONFLICT'
  | 'NDA_GATE_TEMPLATE_NOT_LAUNCHABLE'
  | 'NOT_FOUND'
  | 'STALE_QUESTIONNAIRE_LINK'
  | 'DUPLICATE'
  | 'FORBIDDEN'
  | 'CONFLICT'
  | 'access-denied'
  | 'permission-error'
  | 'invalid-jwt'
  | 'unexpected';

export type FormConfig = DynamicFormFieldConfig<QuestionnaireFormComponentRegistry>[];

export type TaskNotificationParams = {
  taskStatus?: TaskStatus;
  assigneeId?: string;
};

export type NotificationParams =
  | { type: 'finding-comment'; findingId: string; href: string }
  | {
      /** Set when an AI agent performed the action; the UI names the agent
       * as the actor instead of the user whose session it borrowed. */
      actorType: 'agent';
    }
  | {
      type?: 'acknowledgement-request' | 'approval-request' | 'acknowledgement-bulk-request';
      policyInternalIds?: string[];
    }
  | {
      approvalEvent:
        | 'requested'
        | 'auto_withdrawn'
        | 'response_received'
        | 'resent'
        | 'due_soon'
        | 'overdue'
        | 'reminder'
        | 'expiring'
        | 'expired';
      requesterName?: string;
      approverName?: string;
      approverRecordId?: string;
      requireAllApprovers?: boolean;
      singleAssignee?: boolean;
      expirationDate?: string;
      /** Entity (risk/vendor) name for in-app display when relation is null */
      entityName?: string;
    }
  | { controlStatus: ControlStatus }
  | { riskApprovalStatus: Risk_Approval_Statuses_Enum }
  | {
      riskStatus: RiskAssessmentStatus;
    }
  | {
      vendorStatus: VendorAssessmentStatus;
    }
  | { vendorApprovalStatus: Vendor_Approval_Statuses_Enum }
  | {
      questionnaireStatus: Vendor_Questionnaire_Statuses_Enum;
    }
  | TaskNotificationParams
  | {
      policyApprovalStatus:
        | Policy_Approval_Statuses_Enum.Expiring
        | Policy_Approval_Statuses_Enum.Overdue
        | Policy_Approval_Statuses_Enum.Approved;
      approvalReminderTo?: 'approval_creator';
    }
  | {
      policyAcknowledgementStatus:
        | Policy_Acknowledgement_Statuses_Enum.Expiring
        | Policy_Acknowledgement_Statuses_Enum.Overdue;
    }
  | {
      vendorQuestionnaireStatus: Vendor_Questionnaire_Statuses_Enum;
    }
  | {
      vqAfterSubmissionEvent?: 'vq-ai-review-findings-found';
    }
  | {
      clientQuestionnaireStatus: Client_Questionnaire_Status_Enum;
    }
  | {
      policyVersionStatus: Policy_Version_Statuses_Enum;
    }
  | {
      trustPortalAccessRequestEvent: TrustPortalAccessRequestNotificationEvent;
    };

/** `handoff`: automatic NDA review stopped and the pending request awaits manual review. */
export type TrustPortalAccessRequestNotificationEvent = 'created' | 'handoff';

export type AiAutocompleteEntities =
  | 'control-description'
  | 'risk-description'
  | 'risk-treatment-plan'
  | 'risk-treatment-plan-tasks'
  | 'clientq-question-answer'
  | 'policy-text'
  | 'policy-description';

export interface AiAutocompleteEntityOutput<
  TEntity extends AiAutocompleteEntities = AiAutocompleteEntities,
> {
  autocomplete: string;
  extra: AiAutocompleteEntityOutputExtra<TEntity>;
}

export interface AiAutocompleteEntityOutputExtras {
  'clientq-question-answer': { comment: string };
  'risk-treatment-plan-tasks': { tasks: string[] };
}

export type AiAutocompleteEntityOutputExtra<
  TEntity extends AiAutocompleteEntities = AiAutocompleteEntities,
> = TEntity extends keyof AiAutocompleteEntityOutputExtras
  ? AiAutocompleteEntityOutputExtras[TEntity]
  : undefined;

export enum AiCreateEntityActionType {
  Created = 'created',
  Linked = 'linked',
}

export type AiCreateEntityInput =
  | AiCreateTaskFromControlFindingInput
  | AiCreateRiskFromControlFindingInput
  | AiCreateTaskFromVendorFindingInput
  | AiCreateRiskFromVendorFindingInput;

export interface AiCreateTaskFromControlFindingInput {
  entity: 'task-control-finding';
  findingId: string;
}

export interface AiCreateRiskFromControlFindingInput {
  entity: 'risk-control-finding';
  findingId: string;
}

export interface AiCreateTaskFromVendorFindingInput {
  entity: 'task-vendor-finding';
  findingId: string;
}

export interface AiCreateRiskFromVendorFindingInput {
  entity: 'risk-vendor-finding';
  findingId: string;
}

export type VendorQuestionnaireFormAnswerValue =
  | string
  | string[]
  | { file: { id: string; name?: string | null; size?: number | null } };

export interface ClientQquestionMetadata {
  row: number;
  page: null;
  page_name: null;
  answers_header: string;
  comments_header: string;
  possible_answers: string | string[];
  questions_header: string;
  answers_coordinates: [number, number];
  comments_coordinates: [number, number];
  question_coordinates: [number, number];
}

export type SearchOutputData =
  | SearchControlsOutputData
  | SearchEvidenceOutputData
  | SearchPoliciesOutputData
  | SearchRisksOutputData
  | SearchVendorsOutputData
  | SearchTasksOutputData;

export type SearchControlsOutputData = {
  entity: SearchEntitiesEnum.Controls;
  data: ControlsTextSearchDetailsFragment;
};

type SearchEvidenceOutputData = {
  entity: SearchEntitiesEnum.Evidences;
  data: EvidenceTextSearchDetailsFragment;
};

type SearchPoliciesOutputData = {
  entity: SearchEntitiesEnum.Policies;
  data: PoliciesTextSearchDetailsFragment;
};

type SearchRisksOutputData = {
  entity: SearchEntitiesEnum.Risks;
  data: RisksTextSearchDetailsFragment;
};

type SearchVendorsOutputData = {
  entity: SearchEntitiesEnum.Vendors;
  data: VendorsTextSearchDetailsFragment;
};

type SearchTasksOutputData = {
  entity: SearchEntitiesEnum.Tasks;
  data: TasksTextSearchDetailsFragment;
};

export type FileMetadata = {
  lifetimeMinutes?: number;
};

export type IpList = IpAddress[];

export type IpAddress = string;
